/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { SaleService } from '../services/sale-service';
import { salesKeys } from './sales-keys';
import type { Sale } from '../types/sale.types';

export interface SalesList {
  isLoading: boolean;
  isError: boolean;
  sales: Sale[];
  resultsCount: number;
  refresh: () => void;
}

/**
 * Loads the agency's sale records for the listing.
 *
 * Every record is loaded and the panel narrows them here, the way the lease
 * registry does: a multi-select state section has no single-value server
 * filter to map onto, and re-fetching on each chip would make the list churn
 * while the broker is still picking.
 *
 * @param agencyId - The owning agency.
 * @param search - Free-text filter over buyer, property and broker.
 * @returns The view-model: flags, the searched rows and a refresh.
 */
export function useSales(agencyId: string, search: string): SalesList {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: salesKeys.list(agencyId, ''),
    queryFn: () => SaleService.listSales(agencyId),
    enabled: Boolean(agencyId),
  });

  const rows = query.data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((sale) =>
      [sale.buyer_name, sale.property_title, sale.property_id, sale.responsible_broker_email]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q)),
    );
  }, [rows, search]);

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    sales: filtered,
    resultsCount: filtered.length,
    refresh: () => queryClient.invalidateQueries({ queryKey: ['sales', agencyId] }),
  };
}
